import React, { useState } from 'react';

// This is the main component for your application.
// It uses React Hooks (useState) to manage which page is currently displayed.
export default function App() {
  // 'page' state determines whether to show the 'home' or 'about' view.
  // We start by showing the 'home' page.
  const [page, setPage] = useState('home');

  // --- Home Page View ---
  // This is the initial view with the logo and slogan.
  const HomePage = () => (
    <div
      className="flex flex-col items-center justify-center text-center p-8 cursor-pointer group"
      // When this container is clicked, we change the state to show the 'about' page.
      onClick={() => setPage('about')}
    >
      {/* Logo Image:
        - IMPORTANT: Place your  'ioFortis_logo.jpg' file in the `public` folder 
          of your React project. The `src="/ioFortis_logo.jpg"` path will then work correctly.
        - The `onError` attribute provides a fallback image in case the logo fails to load.
        - Responsive sizing: smaller on mobile (w-48), larger on medium screens and up (md:w-64).
        - A subtle hover effect is added for desktop users.
      */}
      <img
        src="/ioFortis_logo.png" // Ensure this path matches your actual logo file in the public folder.
        alt="ioFortis Logo"
        className="w-48 h-48 md:w-64 md:h-64 object-contain transition-transform duration-300 ease-in-out group-hover:scale-105"
        onError={(e) => { 
          e.target.onerror = null; 
          e.target.src='https://placehold.co/256x256/1a1a1a/ffffff?text=ioFortis+Logo'; 
        }}
      />
      {/* Slogan Text:
        - Styled with responsive text size and a light gray color for contrast against the black background.
      */}
      <h1 className="mt-6 text-2xl md:text-3xl font-light text-gray-300 tracking-wider">
        Bravely Innovate, Boldly Solve
      </h1>
    </div>
  );

  // --- About Page View ---
  // This view is shown after the user clicks the logo.
  const AboutPage = () => (
    <div className="flex flex-col items-center justify-center text-center p-8 max-w-4xl mx-auto animate-fade-in">
      <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
        About Us
      </h2>
      <p className="text-lg text-gray-300 mb-8">
        {/* This paragraph contains a clickable span that returns to the home page. */}
        {/* The span is styled to look like a link with hover effects. */}  
        <span
          onClick={() => setPage('home')}
          className="cursor-pointer transition-colors duration-200"
          title="Go back to Home"
        >
          IOFORTIS PTE. LTD. (UEN: 202531128E) was founded on a simple belief: the Cloud & AI should be a powerful asset, not a complex burden. In a world of constant change, you need more than just a provider; you need a brave partner. We are a team of certified cloud architects and engineers who live by our motto: Bravely Innovate, Boldly Solve. We dig deep to understand your vision and have the courage to build the innovative solutions required to make it a reality.
        </span>
        </p>
        {/* Contact Button:
          - This button is now an anchor tag that links to LinkedIn.
          - It opens the link in a new tab.
        */}
      <a
        href="https://www.linkedin.com/company/iofortis"
        target="_blank"
        rel="noopener noreferrer"
        className="px-8 py-3 bg-gray-800 text-white font-semibold rounded-lg shadow-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-75 transition-all duration-300 inline-block"
      >
        &#x2709; Contact over LinkedIn
      </a>
    </div>
  );

  return (
    // Main container for the entire app.
    // - `bg-black`: Sets a black background.
    // - `min-h-screen`: Ensures the container takes up the full height of the screen.
    // - `flex items-center justify-center`: Centers the content vertically and horizontally.
    // - `font-sans`: Uses a standard sans-serif font for a clean look.
    <main className="bg-black min-h-screen w-full flex items-center justify-center font-sans">
      {/* Conditional Rendering:
        - This is the core logic for switching between pages.
        - If the 'page' state is 'home', it renders the HomePage component.
        - Otherwise, it renders the AboutPage component.
      */}
      {page === 'home' ? <HomePage /> : <AboutPage />}
    </main>
  );
}

// Note: To make this a runnable application, you would typically use a setup like Create React App.
// 1. Run `npx create-react-app my-app`
// 2. Replace the contents of `src/App.js` with this code.
// 3. Place your `ioFortis_logo.jpg` inside the `public` folder.
// 4. Run `npm start` to see your app.
// 5. Run `npm run build` to create the static files for S3 hosting.
// 6. Upload the contents of the `build` folder to your S3 bucket.
// 7. Ensure your S3 bucket is configured for static website hosting.
// 8. Access your app via the S3 bucket URL or a custom domain if configured.
// 9. Optionally, set up a CDN for better performance and caching.
// 10. Monitor your app's performance and make improvements as needed.
// 11. Consider adding error handling and logging for production readiness.
// 12. Regularly update dependencies and security patches.
// 13. Explore adding more features like routing, state management, or API integration as needed
// 14. Enjoy building your React application with ioFortis!
// 15. Share your app with the world and gather feedback for future improvements.
// 16. Keep iterating on your design and functionality based on user feedback.
// 17. Consider implementing analytics to track user engagement and behavior.
// 18. Stay updated with the latest React features and best practices to enhance your app.
// 19. Engage with the React community for support and inspiration.
// 20. Have fun coding and creating amazing user experiences with React!
// 21. Remember to document your code and maintain a clean codebase for future reference.
// 22. Explore deploying your app on platforms like Vercel, Netlify, or AWS Amplify for easier CI/CD.

/*
To build this application on Windows with npm:

1. Open a terminal (Command Prompt, PowerShell, or Windows Terminal).
2. Navigate to your project directory:
    cd C:\Users\erdem\OneDrive\ioFortis\web

3. Install dependencies (if you haven't already):
    npm install

4. Build the application for production:
    npm run build

This will create a `build` folder with the static files ready for deployment.
*/